import { groupBy, keyBy, map, mapValues } from 'lodash';

import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import type { ProductType, Recommender } from '@coursera/event-pulse-types';

import { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import type { SearchSource } from 'bundles/search-common/providers/searchTypes';

const DEFAULT_PAGE_NUMBER = '1';
export function inferPageNumberFromParams(currentPageFromParams: string | string[] | null | undefined): string {
  const pageNumber = Array.isArray(currentPageFromParams) ? currentPageFromParams[0] : currentPageFromParams;
  if (pageNumber === '' || pageNumber === null) return DEFAULT_PAGE_NUMBER; // because Number('' | null) === 0
  const number = Number(pageNumber);
  return isNaN(number) || number < 0 ? DEFAULT_PAGE_NUMBER : String(number);
}
export function inferQueryFromParams(queryFromParams: string | string[] | null | undefined): string {
  return String((Array.isArray(queryFromParams) ? queryFromParams[0] : queryFromParams) || '');
}

export type SearchEventingData = {
  searchIndex: string;
  searchRecommender?: Recommender;
  searchIndexPosition: number;
  page: string;
  searchQuery: string;
  filtersApplied: Record<string, string[]>;
  pageSection: {
    sectionName: string;
  };
};

export const getSearchEventingData = ({
  source,
  inferredCurrentPageNumber,
  inferredPageNumberFromParams,
  isInfiniteStylePagination,
  activeFilters,
  searchQuery,
}: {
  source?: SearchSource;
  inferredCurrentPageNumber: number;
  inferredPageNumberFromParams: string;
  isInfiniteStylePagination: boolean;
  activeFilters: (string | undefined)[];
  searchQuery: string;
}): SearchEventingData => {
  const groupedFilters = groupBy(activeFilters, (filter) => (typeof filter === 'string' ? filter.split(':')[0] : ''));
  const keyedFilters = keyBy(groupedFilters, (group) => {
    const firstItem = Array.isArray(group) ? group[0] : undefined;
    return firstItem && typeof firstItem === 'string' ? firstItem.split(':')[0] || '' : '';
  });
  const filtersApplied: Record<string, string[]> = mapValues(keyedFilters, (group) =>
    Array.isArray(group) ? map(group, (filter) => (typeof filter === 'string' ? filter.split(':')[1] || '' : '')) : []
  );
  return {
    searchIndex: source?.indexName || '',
    searchRecommender: source?.recommender
      ? { hash: source.recommender.hash || '', context: source.recommender.context }
      : undefined,
    searchIndexPosition: 1,
    page: isInfiniteStylePagination ? String(inferredCurrentPageNumber) : inferredPageNumberFromParams,
    searchQuery,
    filtersApplied,
    pageSection: {
      sectionName: 'search_frame',
    },
  };
};

/**
 * Extracts event ID from a product ID string
 * Handles special cases for clips and tilde-separated IDs
 *
 * @param id - The product ID string
 * @param type - The product type (optional)
 * @param isSearch - Whether this is a search context (optional)
 * @returns The extracted event ID or 'undefined' if invalid
 */
export const extractEventId = (id: string | undefined | null, type?: ProductType, isSearch?: boolean): string => {
  if (typeof id !== 'string') {
    return 'undefined';
  }
  if (type === 'clip' && isSearch) {
    // Clips in search are split with -
    const lastDash = id?.lastIndexOf('-');
    if (lastDash !== -1) {
      return id?.substring(lastDash + 1);
    }
  } else if (type === 'clip') {
    // clips id in collections are seperated by ~
    const lastTilde = id?.lastIndexOf('~');
    if (lastTilde !== -1) {
      return id?.substring(lastTilde + 1);
    }
  }
  // Parse as a string key so escaped nested ids are unescaped (e.g. s12n~child!~abc -> [s12n, child~abc])
  const tuple = stringKeyToTuple(id);
  // Versioned s12n ids are only unique with their "child~" prefix, so keep it (e.g. child~abc -> child~abc)
  const childIndex = tuple.indexOf('child');
  if (childIndex !== -1 && childIndex < tuple.length - 1) {
    return tuple.slice(childIndex).join('~');
  }
  if (tuple.length > 1 && tuple[1]) {
    return tuple[1];
  }
  return tuple.length === 1 && tuple[0] ? tuple[0] : id;
};

/**
 * Converts a learning product type to its eventing V3 product type
 *
 * @param type - The learning product type
 * @returns The corresponding eventing V3 product type
 */
export const eventingV3ProductType = (type?: string): ProductType | undefined => {
  switch (type) {
    case LEARNING_PRODUCTS.Course:
    case LEARNING_PRODUCTS.GuidedProject:
    case LEARNING_PRODUCTS.Project:
      return 'course';
    case LEARNING_PRODUCTS.Video:
    case LEARNING_PRODUCTS.Lesson:
      return 'clip';
    case LEARNING_PRODUCTS.Specialization:
    case LEARNING_PRODUCTS.ProfessionalCertificate:
      return 's12n';
    case LEARNING_PRODUCTS.Degree:
    case LEARNING_PRODUCTS.PostgraduateDiploma:
      return 'degree';
    case LEARNING_PRODUCTS.UniversityCertificate:
    case LEARNING_PRODUCTS.Mastertrack:
    case LEARNING_PRODUCTS.GraduateCertificate:
      return 'premium_certificate';
    default:
      return undefined;
  }
};

export const buildProductCardEventData = ({
  id,
  name,
  slug,
  productType,
  index,
  isCompareCard,
}: {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  productType?: string | null;
  index: number;
  isCompareCard?: boolean;
}) => {
  const safeId = id ?? '';
  const safeSlug = slug ?? '';
  const type = eventingV3ProductType(productType ?? undefined);
  return {
    product: {
      id: extractEventId(safeId) || safeId,
      name: name ?? '',
      slug: safeSlug,
      ...(type && { type }),
    },
    productCard: {
      index,
      ...(isCompareCard && { isCompareCard: true }),
    },
  };
};

export const buildProductEventData = (args: {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  productType?: string | null;
}) => buildProductCardEventData({ ...args, index: 0 }).product;
